import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ShapesInterface } from '@app/shared/models/shapes.model';
import { Feature, StopsInterface } from '@app/shared/models/stops.model';
import { BusService } from '@app/shared/services/bus.service';
import { version } from '@root/package.json';
import * as L from 'leaflet';
import { NGXLogger } from 'ngx-logger';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { MapService } from './map-services/map.service';
import { variable } from '@angular/compiler/src/output/output_ast';
import { stringify } from 'querystring';

export interface StopsStationsInterface {
  stop_id?: string;
  stop_name?: string;
  stop_code?: string;
  stop_lat?: number;
  stop_lon?: number;
}

@Component({
  selector: 'ty-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  map!: L.Map;
  private marker!: L.Marker;
  isNavbarOpen = false;
  coordenadas: { lat?: number; lng?: number } = {};
  busStops: L.Marker<any>[] = [];
  shapesPolyline!: L.Polyline<any | any, any>[];
  stopsGroup = L.layerGroup();
  stopMarkerSelected!: L.Marker<any>;
  allStopsGroup = L.layerGroup();
  allStations!: StopsStationsInterface[];
  isVisibleAllStation = false;
  isSimulateMode = false;
  viewMap = false;
  notifier = new Subject();
  version: string;
  hideMenu = false;

  constructor(
    private busService: BusService,
    private logger: NGXLogger,
    private mapService: MapService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar
  ) {
    this.version = version;
    this.isSimulateMode = true;
  }

  ngOnInit(): void {
    this.showMenu();
    this.mapService.clearObservable.pipe(takeUntil(this.notifier)).subscribe(
      (data) => {
        this.clearMap();
      },
      (error: Error) => {
        this.logger.error(`MapComponent.ngOnInit=>  ${error}`);
        this.snackBar.open('Error al cargar el Componente del Mapa', 'Aceptar', { duration: 6000 });
        throw error;
      }
    );

    this.mapService.mainObservable.pipe(takeUntil(this.notifier)).subscribe(
      (data: any) => {
        this.setBusStops(data);
      },
      (error: Error) => {
        this.logger.error(`MapComponent.ngOnInit=> ${error}`);
        this.snackBar.open('Error al cargar las paradas', 'Aceptar', { duration: 6000 });
        throw error;
      }
    );

    this.mapService.shapesObservable.pipe(takeUntil(this.notifier)).subscribe(
      (data: any) => {
        this.setBusShapes(data);
      },
      (error: Error) => {
        this.logger.error(`MapComponent.ngOnInit=> ${error}`);
        this.snackBar.open('Error al cargar los puntos del mapa', 'Aceptar', { duration: 6000 });
        throw error;
      }
    );

    this.mapService.ubicationObservable.pipe(takeUntil(this.notifier)).subscribe(
      (data) => {
        this.goToUbication(data);
      },
      (error: Error) => {
        this.logger.error(`MapComponent.ngOnInit=> ${error}`);
        this.snackBar.open('Error al cargar la ubicación del Mapa', 'Aceptar', { duration: 6000 });
        throw error;
      }
    );

    this.createMap();

    this.getAllBusStops();

    if (window.location.href.includes(this.busService.simulatePath)) {
      this.isSimulateMode = true;
    }
  }

  showMenu() {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.hideMenu =
          event.url.split('?')[0] === '/times' || event.url.split('?')[0] === '/stops';
      }
    });
  }

  ngAfterViewInit(): void {
    this.openNav();
    this.onMapReady();
    (window as any).agregarMarcador = (lat: number, lng: number) => this.agregarMarcador(lat, lng);
  }

  ngOnDestroy(): void {
    this.notifier.next();
    this.notifier.complete();
    this.map.remove();
  }

  onMapReady(map?: any): void {
    setTimeout(() => {
      this.map.invalidateSize();
    }, 300);
  }

  createMap(): void {
    if (this.map) {
      this.map.remove();
    }
    // Valencia coordinates
    this.coordenadas.lat = 39.47796176100393;
    this.coordenadas.lng = -0.37456605465674997;

    // Default Zoom Level
    const zoomlevel = 12;

    // Map creation
    this.map = L.map('map', {
      center: [this.coordenadas.lat, this.coordenadas.lng],
      zoomControl: false,
      zoom: zoomlevel,
    });

    L.control
      .zoom({
        position: 'bottomright',
      })
      .addTo(this.map);

    const mainLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      // tileSize: 512,
      // zoomOffset: -1,
      detectRetina: true,
      minZoom: 2,
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyrigth">' +
        'OpenStreetMap</a> contributors',
    });
    mainLayer.addTo(this.map);
  }

  private agregarMarcador(lat: number, lng: number): void {
    const iconoPersonalizado = L.icon({
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      iconSize: [25, 41], // Tamaño del icono
      iconAnchor: [12, 41], // Punto de anclaje del icono
      popupAnchor: [1, -34], // Punto de anclaje del popup
      shadowUrl: '', // Eliminamos la sombra para evitar bordes adicionales
    });

    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    } else {
      this.marker = L.marker([lat, lng], { icon: iconoPersonalizado }).addTo(this.map);
    }

    // En comentario para evitar el centrado
    // this.map.setView([lat, lng], 13);
  }

  clearMap(): void {
    // Point selected
    if (this.stopMarkerSelected) {
      this.map.removeLayer(this.stopMarkerSelected);
    }

    // Stops
    this.stopsGroup.clearLayers();

    // Shapes
    if (this.shapesPolyline && this.shapesPolyline !== []) {
      this.shapesPolyline.forEach((element) => {
        this.map.removeLayer(element);
      });
    }
  }

  openNav(): void {
    const sidebar = document.getElementById('mySidebar');
    const main = document.getElementById('main');
    if (sidebar && main) {
      const screenWidth = window.innerWidth;
      const sidebarWidth = screenWidth > 470 ? 70 : screenWidth > 400 ? 70 : 70;

      if (!this.isNavbarOpen) {
        sidebar.className = 'sidebar sidebar-min';
        sidebar.style.width = `${sidebarWidth}%`; // Ajuste dinámico
        main.className = 'main-container main-container-min';
      } else {
        sidebar.style.width = '0PX';
        main.className = 'main-container main-container-zero';
      }
      this.isNavbarOpen = !this.isNavbarOpen;
    }
  }

  getAllBusStops(): void {
    this.busService
      .getListOfAllStops()
      .pipe(first())
      .subscribe(
        (res: StopsStationsInterface[]) => {
          // Save all stations for then display it
          this.allStations = res;
        },
        (error: Error) => {
          this.logger.error(`MapComponent.getAllBusStops=> ${error}`);
          this.snackBar.open('Error al cargar todas las paradas de Bus', 'Aceptar', {
            duration: 6000,
          });
          throw error;
        }
      );
  }

  displayStops(): void {
    if (this.isVisibleAllStation) {
      this.map.removeLayer(this.allStopsGroup);
      this.isVisibleAllStation = false;
      this.snackBar.open('Ocultando las paradas', 'Aceptar', { duration: 3000 });
    } else {
      if (this.allStations) {
        // console.log(this.allStopsGroup);
        this.snackBar.open('Mostrando todas las paradas', 'Aceptar', { duration: 3000 });
        this.map.removeLayer(this.allStopsGroup);
        if (this.stopMarkerSelected) {
          this.map.removeLayer(this.stopMarkerSelected);
        }

        const marker = L.divIcon({
          className: 'custom-div-icon',
          html: `<div style='
          height: 0.8em;
          width: 0.8em;
          border: 2px solid #353535;
          background-color:  #fff;
          border-radius: 50%;
          '
          onmouseover="this.style.backgroundColor='#cf142b'"
          onmouseout="this.style.backgroundColor='#fff'"
          class='marker-pin puntero'></div>`,
          iconSize: [30, 42],
          iconAnchor: [6, 4],
        });

        // Desde paradas
        let cont = 0;
        this.allStations.forEach((element: any) => {
          const direction = element;
          this.allStopsGroup.addLayer(
            L.marker([element.stop_lat, element.stop_lon], { icon: marker })
              .bindPopup(
                `<div class="direction" href="javascript:void(0)"
              style="font-size: 16px; font-weight: bold; color: #cf142b; cursor: pointer;
              font: 400 18px/25px Roboto">
              <p style="margin-bottom: 0px; font-weight: bold;"> ${element.stop_name} (${element.stop_code}) </p>

              <div style="text-align: right;">
                <span style="color: #4c4c4c; font-size: 14px; margin-right: 5px;">Consultar</span>
                <div style="width: 24px; float: right;">
                  <img src="../../../assets/icons/luc-cuenca-filled-arrow-right.svg" alt="" style="width: 24px;" />
                </div>

              </div>

                </div>`
              )
              .on('click', (a) => {
                const popUp = a.target.getPopup();
                popUp
                  .getElement()
                  .querySelector('.direction')
                  .addEventListener('click', (e: any) => {
                    this.goToStopViewFromAllStations(direction);
                  });
              })
          );
          cont++;
        });

        this.map.addLayer(this.allStopsGroup);
        this.isVisibleAllStation = true;
      }
    }
  }

  openSnackBar(): void {
    this.snackBar.open('La localizacion esta deshabilitada', 'Ok', {
      duration: 5000,
    });
  }

  goToMyUbication(): void {
    if (!navigator.geolocation) {
      this.snackBar.open(
        'La funcion de localizacion no esta disponible en este dispositivo',
        'Ok',
        {
          duration: 2000,
        }
      );
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (this.stopMarkerSelected) {
            this.map.removeLayer(this.stopMarkerSelected);
          }
          const marker = L.icon({
            iconUrl: '../../../assets/icons/marker.svg',
            // shadowUrl: '../../../assets/icons/point-map-shadow.png',
            iconSize: [40, 40], // size of the icon
            // shadowSize: [30, 30], // size of the shadow
            iconAnchor: [20, 40], // point of the icon which will correspond to marker's location
            // shadowAnchor: [10, 10],  // the same for the shadow
            popupAnchor: [-10, -10], // point from which the popup should open relative to the iAnchor
          });

          this.stopMarkerSelected = L.marker(
            [position.coords.latitude, position.coords.longitude],
            { icon: marker }
          ).addTo(this.map);

          this.map.flyTo([position.coords.latitude, position.coords.longitude], 13);
        },
        (error) => {
          // alert('Error occurred. Error code: ' + error.code);
          // error.code can be:
          //   0: unknown error
          //   1: permission denied
          //   2: position unavailable (error response from location provider)
          //   3: timed out

          switch (error.code) {
            case 0:
              this.snackBar.open('Error desconocido', 'Ok', {
                duration: 5000,
              });
              break;
            case 1:
              this.snackBar.open('Permiso denegado', 'Ok', {
                duration: 5000,
              });
              break;
            case 2:
              this.snackBar.open(
                'Posicion no disponible (error response from location provider)',
                'Ok',
                {
                  duration: 5000,
                }
              );
              break;
            case 3:
              this.snackBar.open('Timed out', 'Ok', {
                duration: 5000,
              });
              break;
          }
        }
      );
    }
  }

  goToUbication(data: any): void {
    if (this.stopMarkerSelected) {
      this.map.removeLayer(this.stopMarkerSelected);
    }
    const marker = L.icon({
      iconUrl: '../../../assets/icons/marker.svg',
      // shadowUrl: '../../../assets/icons/point-map-shadow.png',
      iconSize: [40, 40], // size of the icon
      // shadowSize: [30, 30], // size of the shadow
      iconAnchor: [20, 40], // point of the icon which will correspond to marker's location
      // shadowAnchor: [10, 10],  // the same for the shadow
      popupAnchor: [-10, -10], // point from which the popup should open relative to the iconAnchor
    });

    this.stopMarkerSelected = L.marker([data[1], data[0]], { icon: marker }).addTo(this.map);
    this.map.flyTo([data[1], data[0]], 16);
  }

  goToStopView(stop: any): void {
    const busId = Number(this.route.snapshot.queryParamMap.get('bus'));
    const busname = String(this.route.snapshot.queryParamMap.get('busName'));
    const busCode = String(this.route.snapshot.queryParamMap.get('busCode'));

    if (this.router.url.includes(this.busService.simulatePath)) {
      this.router.navigate([this.busService.simulatePath, 'times'], {
        queryParams: {
          bus: busId,
          busName: busname,
          busCode,
          stop: stop.properties.stop_id,
          stopName: stop.properties.stop_name,
          stopCode: stop.properties.stop_code,
          lon: stop.geometry.coordinates[0],
          lat: stop.geometry.coordinates[1],
        },
      });
    } else {
      this.router.navigate(['/times'], {
        queryParams: {
          bus: busId,
          busName: busname,
          busCode,
          stop: stop.properties.stop_id,
          stopName: stop.properties.stop_name,
          stopCode: stop.properties.stop_code,
          lon: stop.geometry.coordinates[0],
          lat: stop.geometry.coordinates[1],
        },
      });
    }

    if (!this.isNavbarOpen) {
      this.openNav();
    }
  }

  goToStopViewFromAllStations(stop: any): void {
    const busCode = String(this.route.snapshot.queryParamMap.get('busCode'));

    if (this.router.url.includes(this.busService.simulatePath)) {
      this.router.navigate([this.busService.simulatePath, 'times'], {
        queryParams: {
          stop: stop.stop_id,
          stopName: stop.stop_name,
          stopCode: stop.stop_code,
          lon: stop.stop_lon,
          lat: stop.stop_lat,
        },
      });
    } else {
      this.router.navigate(['/times'], {
        queryParams: {
          stop: stop.stop_id,
          stopName: stop.stop_name,
          stopCode: stop.stop_code,
          lon: stop.stop_lon,
          lat: stop.stop_lat,
        },
      });
    }

    if (!this.isNavbarOpen) {
      this.openNav();
    }
  }

  setBusStops(stopsList: StopsInterface): void {
    const data: number[][] = [];

    stopsList.features.forEach((element: Feature) => {
      data.push(element.geometry.coordinates);
    });

    this.stopsGroup.clearLayers();
    this.map.removeLayer(this.stopsGroup);

    // All stops layer
    this.map.removeLayer(this.allStopsGroup);
    this.isVisibleAllStation = false;

    if (this.stopMarkerSelected) {
      this.map.removeLayer(this.stopMarkerSelected);
    }

    const myIcon = L.divIcon({
      className: 'custom-div-icon',
      html: `<div style='
      height: 0.8em;
      width: 0.8em;
      border-radius: 50%;
      'class='marker-pin puntero'
      ></div>`,
      iconSize: [30, 42],
      iconAnchor: [6, 4],
    });

    // console.log(myIcon);
    // Desde Linea
    let cont = 0;
    data.forEach((element: any) => {
      const direction = stopsList.features[cont];
      this.stopsGroup.addLayer(
        L.marker([element[1], element[0]], { icon: myIcon })
          .bindPopup(
            `<div class="direction" href="javascript:void(0)"
          style="font-size: 16px; font-weight: bold; color: #cf142b; cursor: pointer;
          font: 400 18px/25px Roboto">
          <p style="margin-bottom: 0px; font-weight: bold;">  ${stopsList.features[cont].properties.stop_name} (${stopsList.features[cont].properties.stop_code})</p>

          <div style="text-align: right;">
            <span style="color: #4c4c4c; font-size: 14px; margin-right: 5px;">Consultar</span>
            <div style="width: 24px; float: right;">
              <img src="../../../assets/icons/luc-cuenca-filled-arrow-right.svg" alt="" style="width: 24px;" />
            </div>
          </div>
            </div>`
          )
          .on('click', (a) => {
            const popUp = a.target.getPopup();
            popUp
              .getElement()
              .querySelector('.direction')
              .addEventListener('click', (e: any) => {
                this.goToStopView(direction);
              });
          })
      );
      cont++;
    });

    this.map.addLayer(this.stopsGroup);
    if (data.length > 0) {
      if (data && data[0][1] && data[0][0]) {
        this.map.flyTo([data[0][1], data[0][0]], 13);
      }
    } else {
      this.logger.error(`Empty object.`);
    }
  }

  setBusShapes(shapesList: ShapesInterface): void {
    if (this.shapesPolyline && this.shapesPolyline !== []) {
      this.shapesPolyline.forEach((element) => {
        this.map.removeLayer(element);
      });
    }
    this.shapesPolyline = [];
    shapesList.features.forEach((element: any) => {
      const correctList: any[] = [];
      element.geometry.coordinates.forEach((coordinate: any) => {
        correctList.push([coordinate[1], coordinate[0]]);
        // ! Marker per les Dif
        //   const customMarkerStyle = {
        //     radius: 8,
        //     color: 'red',
        //     fillColor: 'yellow',
        //     fillOpacity: 1
        // };
        // L.marker([coordinate[1], coordinate[0]]).addTo(this.map);
      });
      // console.log("Corrected List:",JSON.stringify(correctList));

      // Colour Lines
      const shapes = new L.Polyline(correctList, {
        color: element.properties.route_color,
        weight: 5,
        opacity: 0.9,
        smoothFactor: 1,
      });

      this.shapesPolyline.push(shapes);
      this.map.addLayer(shapes);
    });
  }
}
